import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  useMediaQuery
} from "@mui/material";
import Cropper from "react-easy-crop";
import { useSnackbar } from "../../App"; // Adjust path based on your project structure
import { getAllBrandLists } from "../../service/brandService";
import { getAllCategoryLists, getAllSubCategory } from "../../service/categoryService";
import { getProductById, updateProduct } from "../../service/productService";
import Color from "../../components/Color";

// Helper to crop image to fixed size 185x140
const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Final fixed size
      canvas.width = 185;
      canvas.height = 140;

      // Enable high quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Draw cropped image
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        185,
        140
      );

      // Save as PNG (lossless)
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error("Canvas is empty"));

          const file = new File([blob], "cropped.png", { type: "image/png" });
          const url = URL.createObjectURL(blob);

          resolve({ file, url });
        },
        "image/png", // PNG for best quality
        1.0 // Quality at max
      );
    };

    image.onerror = (err) => reject(err);
  });
};

const EditProduct = ({ open, onClose, productId, onUpdate }) => {
  const { showSnackbar } = useSnackbar();
  const initialFormState = {
    name: "",
    product_code: "",
    base_price: "",
    tax_rate: "",
    brand_id: "",
    category_id: "",
    description: "",
    is_active: true,
    sub_category_id: "",
  };

  const [form, setForm] = useState(initialFormState);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

   const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!open) return;

    const fetchDropdowns = async () => {
      try {
        const brandRes = await getAllBrandLists();
        const categoryRes = await getAllCategoryLists();
        const subcategory = await getAllSubCategory();
        setBrands(Array.isArray(brandRes?.data?.data) ? brandRes.data.data : []);
        setCategories(Array.isArray(categoryRes?.data?.data) ? categoryRes.data.data : []);
        setSubCategories(Array.isArray(subcategory?.data?.data) ? subcategory.data.data : []);
      } catch {
        showSnackbar("Failed to load dropdown data", "error");
      }
    };

    const fetchProduct = async () => {
      try {
        const res = await getProductById(productId);
        const product = res.data.data;

        setForm({
          name: product.name || "",
          product_code: product.product_code || "",
          base_price: product.base_price || "",
          tax_rate: product.tax_rate !== null ? String(product.tax_rate) : "",
          brand_id: product.brand_id || "",
          category_id: product.category_id || "",
          description: product.description || "",
          is_active: product.is_active ?? true,
          sub_category_id: product.sub_category_id || "",
        });

        setUploadedImageUrl(product.image_url || null);
        setErrors({});
        setImageFile(null);
        setRawImageUrl(null);
        setCropDialogOpen(false);
        setCroppedAreaPixels(null);
      } catch {
        showSnackbar("Failed to fetch product details", "error");
      }
    };

    fetchDropdowns();
    fetchProduct();
  }, [open, productId, showSnackbar]);

  const resetForm = () => {
    setForm(initialFormState);
    setErrors({});
    setImageFile(null);
    setUploadedImageUrl(null);
    setRawImageUrl(null);
    setCropDialogOpen(false);
    setCroppedAreaPixels(null);
  };

  const handleChange = (field, value) => {
    if (field === "name") {
      const filteredValue = value.replace(/[^a-zA-Z ]/g, "");
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
      if (/[^a-zA-Z ]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters are allowed"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (field === "base_price" || field === "tax_rate") {
      const filteredValue = value.replace(/[^0-9.]/g, "");
      setForm((prev) => ({ ...prev, [field]: filteredValue }));
      if (/[^0-9.]/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          [field]: ["Only numbers are allowed"],
        }));
      } else if (field === "base_price" && (filteredValue === "" || parseFloat(filteredValue) <= 0)) {
        setErrors((prev) => ({
          ...prev,
          base_price: ["Price must be greater than 0"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } else {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      if (field === "category_id") {
        setForm((prev) => ({ ...prev, sub_category_id: "" }));
      }
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showSnackbar("Please upload a valid image file", "error");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar("Image size must be less than 5MB", "error");
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      if (width === 185 && height === 140) {
        setImageFile(file);
        setUploadedImageUrl(objectUrl);
        setCropDialogOpen(false);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setRawImageUrl(reader.result);
          setCropDialogOpen(true);
        };
        reader.readAsDataURL(file);
      }
    };

    img.onerror = () => {
      showSnackbar("Failed to read image dimensions", "error");
      URL.revokeObjectURL(objectUrl);
    };
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async () => {
    try {
      const { file, url } = await getCroppedImg(rawImageUrl, croppedAreaPixels);
      setImageFile(file);
      setUploadedImageUrl(url);
      setCropDialogOpen(false);
    } catch {
      showSnackbar("Failed to crop image", "error");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = ["Product name is required"];
    else if (!/^[a-zA-Z ]+$/.test(form.name)) newErrors.name = ["Only letters are allowed"];

    if (!form.product_code.trim()) newErrors.product_code = ["Product code is required"];

    if (!form.base_price) newErrors.base_price = ["Price is required"];
    else if (parseFloat(form.base_price) <= 0) newErrors.base_price = ["Price must be greater than 0"];

    if (form.tax_rate && isNaN(parseFloat(form.tax_rate))) {
      newErrors.tax_rate = ["Tax rate must be a valid number"];
    } else if (form.tax_rate && parseFloat(form.tax_rate) < 0) {
      newErrors.tax_rate = ["Tax rate cannot be negative"];
    }

    if (!form.brand_id) newErrors.brand_id = ["Brand is required"];
    if (!form.category_id) newErrors.category_id = ["Category is required"];
    if (!form.description.trim()) newErrors.description = ["Description is required"];

    const selectedCategory = categories.find((cat) => cat.id === form.category_id);
    if (selectedCategory?.name?.toLowerCase() === "cake" && !form.sub_category_id) {
      newErrors.sub_category_id = ["Subcategory is required for cake"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    try {
      let payload;
      let isMultipart = !!imageFile;

      if (isMultipart) {
        payload = new FormData();
        Object.entries(form).forEach(([key, value]) => {
          payload.append(key, value);
        });
        payload.append("image", imageFile);
      } else {
        payload = {
          ...form,
          remove_image: false,
        };
      }

      await updateProduct(productId, payload, isMultipart);

      const res = await getProductById(productId);
      const updatedProduct = res.data.data;

      setForm({
        name: updatedProduct.name || "",
        product_code: updatedProduct.product_code || "",
        base_price: updatedProduct.base_price || "",
        tax_rate: updatedProduct.tax_rate !== null ? String(updatedProduct.tax_rate) : "",
        brand_id: updatedProduct.brand_id || "",
        category_id: updatedProduct.category_id || "",
        description: updatedProduct.description || "",
        is_active: updatedProduct.is_active ?? true,
        sub_category_id: updatedProduct.sub_category_id || "",
      });

      setUploadedImageUrl(updatedProduct.image_url || null);
      setErrors({});
      setImageFile(null);
      setRawImageUrl(null);
      setCropDialogOpen(false);
      setCroppedAreaPixels(null);

      showSnackbar("Product updated successfully!", "success");
      if (onUpdate) onUpdate();
      setTimeout(() => onClose(), 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        showSnackbar("Validation failed. Please check the form.", "error");
      } else {
        showSnackbar("Something went wrong while updating the product", "error");
      }
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          onClose();
        }}
        PaperProps={{
          sx: { width: "1100px", maxWidth: "95vw", borderRadius: "8px" },
        }}
      >
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 2 },
            maxHeight: { xs: "70vh", sm: "auto" },
            overflowY: "auto",
          }}
        >
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              {/* Row 1: Product Name and Product Code */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Product Name*
                </Typography>
                <TextField
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Enter product name"
                  error={!!errors.name}
                  helperText={errors.name?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 0.8 }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Product Code*
                </Typography>
                <TextField
                  value={form.product_code}
                  onChange={(e) => handleChange("product_code", e.target.value)}
                  placeholder="Enter product code"
                  error={!!errors.product_code}
                  helperText={errors.product_code?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 0.8 }}
                />
              </Box>

              {/* Row 2: Price and Tax Rate */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Price*
                </Typography>
                <TextField
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.base_price}
                  onChange={(e) => handleChange("base_price", e.target.value)}
                  placeholder="Enter price"
                  error={!!errors.base_price}
                  helperText={errors.base_price?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 0.8 }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Tax Rate (%)
                </Typography>
                <TextField
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.tax_rate}
                  onChange={(e) => handleChange("tax_rate", e.target.value)}
                  placeholder="Enter tax rate"
                  error={!!errors.tax_rate}
                  helperText={errors.tax_rate?.[0]}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 0.8 }}
                />
              </Box>

              {/* Row 3: Brand and Category */}
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Brand*
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.brand_id} sx={{ mb: 0.8 }}>
                  <Select
                    value={form.brand_id ?? ""}
                    onChange={(e) => handleChange("brand_id", e.target.value)}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Box sx={{ color: "text.secondary" }}>
                            Select brand
                          </Box>
                        );
                      }
                      return brands.find((brand) => brand.id === value)?.name || "";
                    }}
                  >
                    <MenuItem value="">
                      Select brand
                    </MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.brand_id && <FormHelperText>{errors.brand_id[0]}</FormHelperText>}
                </FormControl>
              </Box>
              <Box>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Category*
                </Typography>
                <FormControl fullWidth size="small" error={!!errors.category_id} sx={{ mb: 0.8 }}>
                  <Select
                    value={form.category_id ?? ""}
                    onChange={(e) => {
                      handleChange("category_id", e.target.value);
                      handleChange("sub_category_id", "");
                    }}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Box sx={{ color: "text.secondary" }}>
                            Select category
                          </Box>
                        );
                      }
                      return categories.find((cat) => cat.id === value)?.name || "";
                    }}
                  >
                    <MenuItem value="">
                      Select category
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.category_id && <FormHelperText>{errors.category_id[0]}</FormHelperText>}
                </FormControl>
              </Box>

              {/* Row 4: Cake Subcategory and Description */}
              {(() => {
                const selectedCategory = categories.find((cat) => cat.id === form.category_id);
                if (selectedCategory?.name?.toLowerCase() === "cake") {
                  return (
                    <>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                        >
                          Subcategory*
                        </Typography>
                        <FormControl fullWidth size="small" error={!!errors.sub_category_id} sx={{ mb: 0.8 }}>
                          <Select
                            value={form.sub_category_id ?? ""}
                            onChange={(e) => handleChange("sub_category_id", e.target.value)}
                            displayEmpty
                            renderValue={(value) => {
                              if (!value) {
                                return (
                                  <Box sx={{ color: "text.secondary" }}>
                                    Select subcategory
                                  </Box>
                                );
                              }
                              return subCategories.find((subcat) => subcat.id === value)?.name || "";
                            }}
                          >
                            <MenuItem value="">
                              Select subcategory
                            </MenuItem>
                            {subCategories.map((subcat) => (
                              <MenuItem key={subcat.id} value={subcat.id}>
                                {subcat.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.sub_category_id && (
                            <FormHelperText>{errors.sub_category_id[0]}</FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                        >
                          Description*
                        </Typography>
                        <TextField
                          multiline
                          rows={3}
                          value={form.description}
                          onChange={(e) => handleChange("description", e.target.value)}
                          placeholder="Enter description"
                          error={!!errors.description}
                          helperText={errors.description?.[0]}
                          fullWidth
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          sx={{
                            "& .MuiInputLabel-root": { display: "none" },
                            mb: 0.8,
                            "& .MuiInputBase-inputMultiline": {
                              paddingTop: "8px",
                              paddingBottom: "8px",
                              lineHeight: 1.5,
                            },
                          }}
                        />
                      </Box>
                    </>
                  );
                }
                return (
                  <>
                    <Box sx={{ gridColumn: { sm: "span 2" } }}>
                      <Typography
                        variant="body1"
                        sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                      >
                        Description*
                      </Typography>
                      <TextField
                        multiline
                        rows={3}
                        value={form.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        placeholder="Enter description"
                        error={!!errors.description}
                        helperText={errors.description?.[0]}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          "& .MuiInputLabel-root": { display: "none" },
                          mb: 0.8,
                          "& .MuiInputBase-inputMultiline": {
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            lineHeight: 1.5,
                          },
                        }}
                      />
                    </Box>
                  </>
                );
              })()}
            </Box>

            {/* Active/Inactive Switch */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
              >
                Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => handleChange("is_active", e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: Color.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: Color.primary.main,
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: form.is_active ? Color.primary.main : "text.secondary",
                      fontWeight: form.is_active ? "bold" : "normal",
                    }}
                  >
                    {form.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Box>

            {/* Image Upload */}
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
              >
                Product Image (optional)
              </Typography>
              <Button variant="contained" component="label" sx={{ mb: 0.8, background: Color.primary.main }}>
                Choose Image
                <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
              </Button>
              {errors.image &&
                errors.image.map((msg, i) => (
                  <Typography key={i} variant="caption" color="error" sx={{ display: "block" }}>
                    {msg}
                  </Typography>
                ))}
              <Typography sx={{ fontSize: 10, mt: 0 }}>
                Upload image with dimensions 185x140 pixels
              </Typography>
            </Box>

            {/* Image Preview */}
            {uploadedImageUrl && (
              <Box mt={1}>
                <Typography
                  variant="body1"
                  sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                >
                  Current Product Image
                </Typography>
                <img
                  src={uploadedImageUrl}
                  alt="Uploaded Preview"
                  style={{ width: "30%", maxHeight: 230, objectFit: "cover", borderRadius: 4 }}
                />
              </Box>
            )}

            {/* Submit and Cancel Buttons */}
            <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
              <Button
                onClick={handleCancel}
                sx={{
                  background: "none",
                  color: Color.secondary.main,
                  border: "1px solid",
                  borderColor: Color.secondary.main,
                }}
              >
                Cancel
              </Button>
              <Button type="submit" sx={{ background: Color.primary.main, color: "white" }}>
                Update
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Crop Dialog */}
      <Dialog open={cropDialogOpen} onClose={() => setCropDialogOpen(false)} fullWidth maxWidth="sm"   PaperProps={{
          sx: { width: isMobile ? "90vw" : "300", borderRadius: "8px" },
        }}>
        <DialogTitle>Crop Image</DialogTitle>
        <DialogContent>
          <div style={{ position: "relative", width: "100%", height: 300 }}>
            <Cropper
              image={rawImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={185 / 140}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              sx={{ border: "1px solid", borderColor: Color.secondary.main, color: Color.secondary.main }}
              onClick={() => setCropDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              sx={{ background: Color.primary.main, color: "white" }}
              onClick={handleCropConfirm}
            >
              Crop
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditProduct;